// client/src/features/auth/ResendVerification.tsx — „Retrimiteți linkul" · D-2760, D-2761.
//
// Three screens ask for another confirmation link: the signup's pending panel (D-2758), the
// login when it meets an unconfirmed account (D-2760), and the confirmation page when the link
// it was opened with is spent (D-2759). They share the call, the pause and the sentence.
//
// ── the answer is always 202 (D-2751) ──────────────────────────────────────────────────────
// `POST /auth/email/resend` answers the same whether the address has an account, is already
// confirmed, or has had as many links as its allowance permits — an answer that differed would
// tell a stranger which addresses have accounts waiting. So the screen cannot know whether a
// link went, and does not pretend to: the confirmation is one neutral sentence
// (`auth.verifyEmail.resent`) on every screen, the signup's included.
//
// ── the pause is the server's (D-2761) ─────────────────────────────────────────────────────
// `EmailRationing` lets the confirmation link go once a minute per address. A click inside that
// minute is answered 202 and sends nothing, which the person would read as „the button does not
// work". So after a send the button is disabled for the same sixty seconds and counts them
// down, as SCR-AUTH-003's „Retrimite codul" does for its thirty (D-2674); the test reads the
// sixty off the C#. The per-caller login limiter's 429 — both routes sit under it — starts the
// same pause, because its own sentence already says to wait a minute.

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { authApi } from '@/api/auth';
import { ApiError } from '@/api/http';

import { useAuthErrorMessage } from './errors';
import { useCooldown } from './use-cooldown';

/**
 * How long a resend waits after the last send — the `email-verification` cooldown of
 * `EmailRationing` (D-2751), mirrored here and held to the C# by `register-form.test.tsx`.
 */
export const VERIFICATION_RESEND_COOLDOWN_SECONDS = 60;

export interface ResendVerification {
  /** Asks for another link for this address, carrying the screen's return path. */
  resend: (email: string) => Promise<void>;
  sending: boolean;
  /** Whether a request was answered — NOT whether a mail went; see the header. */
  sent: boolean;
  failure: string | null;
  secondsLeft: number;
}

/**
 * The resend, its pause and its outcome (D-2761). `returnTo` is the deep link the new link must
 * carry back, already passed through `returnPath` by the caller (D-2762); `runningFromMount`
 * starts the pause with the screen, for the panel a fresh send has just led to.
 */
export function useResendVerification(
  returnTo: string | undefined,
  runningFromMount = false,
): ResendVerification {
  const describe = useAuthErrorMessage();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [secondsLeft, startCooldown] = useCooldown(
    VERIFICATION_RESEND_COOLDOWN_SECONDS,
    runningFromMount,
  );

  const resend = useCallback(
    async (email: string) => {
      setSending(true);
      setFailure(null);
      try {
        await authApi.resendEmailVerification(email, returnTo);
        setSent(true);
        startCooldown();
      } catch (error) {
        if (error instanceof ApiError && error.status === 429) startCooldown();
        setFailure(describe(error));
      } finally {
        setSending(false);
      }
    },
    [describe, returnTo, startCooldown],
  );

  return { resend, sending, sent, failure, secondsLeft };
}

/**
 * The button, the neutral sentence after it, and a failure under both. The labels are the
 * caller's, because the login needs the longer „Retrimiteți linkul de confirmare" where the
 * signup's panel — which has just said what the link is for — says „Retrimiteți linkul".
 */
export function ResendVerificationButton({
  email,
  returnTo,
  label,
  countdownLabel,
  runningFromMount = false,
}: {
  email: string;
  returnTo: string | undefined;
  label: string;
  countdownLabel: (seconds: number) => string;
  runningFromMount?: boolean;
}) {
  const { t } = useTranslation();
  const { resend, sending, sent, failure, secondsLeft } = useResendVerification(
    returnTo,
    runningFromMount,
  );

  return (
    <div className="space-y-3">
      {/* Disabled while a pause runs, with the seconds on the label rather than beside it: the
          number is the reason the button cannot be pressed (D-2674, D-2761). */}
      <button
        type="button"
        className="btn btn-outline btn-sm"
        onClick={() => void resend(email)}
        disabled={sending || secondsLeft > 0}
      >
        {secondsLeft > 0 ? countdownLabel(secondsLeft) : label}
      </button>

      {sent ? (
        <p className="text-sm text-pretty text-gray-600" role="status">
          {t('auth.verifyEmail.resent')}
        </p>
      ) : null}

      {failure ? (
        <div className="alert alert-error text-sm" role="alert">
          {failure}
        </div>
      ) : null}
    </div>
  );
}
